import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import './Co2Report.scss';
import Flatpickr from 'react-flatpickr';
import dayjs from 'dayjs';
import { TabsComponent } from '../../../ReusableComponents/Tabs/TabsComponent';
import { fetchCo2DataAsync } from '../../../fetch/reportFetch';
import {
  Co2PerRegionDayType,
  Co2ReportInterface,
  SerializedDataGraphAndAvg,
} from './Co2Report.types';
import { SingleTabInterface } from '../../../ReusableComponents/Tabs/TabsComponent.types';
import { ResponsiveLine } from '@nivo/line';
import { Loader } from '../../../components/Loader/Loader';
import { sortRegionsForView } from '../../../utils/reportUtils';
import { Helmet } from 'react-helmet';
import { ButtonComponent } from '../../../ReusableComponents/Button/ButtonComponent';

function serializeDataForGraphAndAvgPeriod(
  co2SerializedData: Co2PerRegionDayType,
  chartRegion: string
): SerializedDataGraphAndAvg {
  if (chartRegion === 'COMBINED') {
    const dataForGraph = Object.keys(co2SerializedData)
      .filter(regionKey => regionKey !== 'TOTAL')
      .map((region, ix) => ({
        id: `${region}`,
        data: Object.keys(co2SerializedData[region])
          .sort()
          .map(day => ({
            x: day,
            y: co2SerializedData[region][day].avgCo2.toFixed(2),
          })),
      }));
    const avgOfSelectedPeriod =
      Object.keys(co2SerializedData.TOTAL)
        .sort()
        .reduce((acc, cur) => acc + co2SerializedData.TOTAL[cur].avgCo2, 0) /
      Object.keys(co2SerializedData.TOTAL).length;
    return { avgOfSelectedPeriod, dataForGraph };
  } else {
    const dataForGraph = [
      {
        id: chartRegion,
        data: Object.keys(co2SerializedData[chartRegion])
          .sort()
          .map(day => ({
            x: day,
            y: co2SerializedData[chartRegion][day].avgCo2.toFixed(2),
          })),
      },
    ];
    const avgOfSelectedPeriod =
      Object.keys(co2SerializedData[chartRegion])
        .sort()
        .reduce(
          (acc, cur) => acc + co2SerializedData[chartRegion][cur].avgCo2,
          0
        ) / Object.keys(co2SerializedData[chartRegion]).length;
    return { avgOfSelectedPeriod, dataForGraph };
  }
}

function createChart(
  co2SerializedData: Co2PerRegionDayType,
  zoneRegion: string
): JSX.Element | null {
  if (Object.keys(co2SerializedData).length) {
    const { avgOfSelectedPeriod, dataForGraph } =
      serializeDataForGraphAndAvgPeriod(co2SerializedData, zoneRegion);
    return (
      <div style={{ height: '800px' }}>
        <ResponsiveLine
          data={dataForGraph}
          margin={{ top: 50, right: 10, bottom: 120, left: 50 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 'auto',
            max: 'auto',
            stacked: false,
            reverse: false,
          }}
          markers={[
            {
              axis: 'y',
              value: avgOfSelectedPeriod,
              textStyle: {
                fontWeight: 700,
                fill: 'rgba(0,0,0,0.6)',
              },
              lineStyle: { strokeWidth: 2, stroke: 'rgba(0,0,0,0.3)' },
              legend: `Period Avg: ${avgOfSelectedPeriod.toFixed(2)}`,
            },
          ]}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            orient: 'bottom',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
          }}
          axisLeft={{
            orient: 'left',
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: `Avg CO2(g/delivery)`,
            legendOffset: -40,
            legendPosition: 'middle',
          }}
          colors={{ scheme: 'pastel1' }}
          pointSize={10}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabel="y"
          pointLabelYOffset={-12}
          useMesh={true}
          enableSlices="x"
          sliceTooltip={({ slice }) => {
            return (
              <div
                style={{
                  background: 'white',
                  padding: '9px 12px',
                  border: '1px solid #ccc',
                }}
              >
                {slice.points.map(point => (
                  <div
                    key={point.id}
                    style={{
                      padding: '3px 0',
                    }}
                  >
                    <span style={{ fontWeight: 500, color: point.serieColor }}>
                      {point.serieId}:{' '}
                    </span>
                    <span style={{ fontWeight: 400 }}>
                      {point.data.yFormatted}
                    </span>
                  </div>
                ))}
              </div>
            );
          }}
          layers={[
            'grid',
            'axes',
            'areas',
            'crosshair',
            'lines',
            'points',
            'slices',
            'mesh',
            'markers',
            'legends',
          ]}
          legends={[
            {
              anchor: 'bottom',
              direction: 'row',
              justify: false,
              translateX: 0,
              translateY: 100,
              itemsSpacing: 100,
              itemDirection: 'left-to-right',
              itemWidth: 80,
              itemHeight: 20,
              itemOpacity: 0.75,
              symbolSize: 12,
              symbolShape: 'circle',
              symbolBorderColor: 'rgba(0, 0, 0, .5)',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
        />
      </div>
    );
  }
  return null;
}

function createTabsWithChart(
  co2SerializedData: Co2PerRegionDayType
): SingleTabInterface[] {
  const tabs = Object.keys(co2SerializedData)
    .sort((a, b) => sortRegionsForView(a, b))
    .map(zone => ({
      tabClassName: 'co2-tab',
      label: zone,
      tabValue: zone,
      tabPanelNode: createChart(co2SerializedData, zone),
    }));
  tabs.push({
    tabClassName: 'co2-tab',
    label: 'COMBINED',
    tabValue: 'COMBINED',
    tabPanelNode: createChart(co2SerializedData, 'COMBINED'),
  });
  return tabs;
}

function doCheckSelectedPeriod(selectedDates: Date[], instance: any): void {
  if (selectedDates != null && selectedDates.length === 1) {
    const startDate = dayjs(selectedDates[0]);
    const maxDate = startDate.add(30, 'day');
    const minDate = startDate.subtract(30, 'day');
    instance.set('minDate', minDate.toISOString());
    instance.set('maxDate', maxDate.toISOString());
    instance.jumpToDate(minDate.toISOString(), true);
  }
}

const Co2Report: FunctionComponent = () => {
  const [date, setDate] = useState<Date[]>([
    dayjs().subtract(30, 'day').toDate(),
    dayjs().toDate(),
  ]);
  const [co2SerializedData, setCo2Data] = useState<Co2PerRegionDayType | null>(
    null
  );
  const [selectedTab, setSelectedTab] = useState<string>('TOTAL');

  const calcAvgCo2PerRegionDay = (data: Co2ReportInterface[]): void => {
    const co2PerRegionDay: Co2PerRegionDayType = { TOTAL: {} };
    data.forEach(bundleData => {
      const { co2PerDelivery } = bundleData;
      const region = bundleData.region;
      const deliveryDay = bundleData.deliveryDay.toString();
      if (co2PerRegionDay.TOTAL.hasOwnProperty(deliveryDay)) {
        co2PerRegionDay.TOTAL[deliveryDay].bundles.push(bundleData);
        const bundlesLength = co2PerRegionDay.TOTAL[deliveryDay].bundles.length;
        co2PerRegionDay.TOTAL[deliveryDay].sumOfCo2 +=
          bundleData.co2PerDelivery;
        const { sumOfCo2 } = co2PerRegionDay.TOTAL[deliveryDay];
        co2PerRegionDay.TOTAL[deliveryDay].avgCo2 = sumOfCo2
          ? sumOfCo2 / bundlesLength
          : sumOfCo2;
      } else {
        co2PerRegionDay.TOTAL = {
          ...co2PerRegionDay.TOTAL,
          [deliveryDay.toString()]: {
            bundles: [bundleData],
            avgCo2: co2PerDelivery || 0,
            sumOfCo2: co2PerDelivery || 0,
          },
        };
      }
      if (co2PerRegionDay.hasOwnProperty(region)) {
        if (co2PerRegionDay[region].hasOwnProperty(deliveryDay)) {
          co2PerRegionDay[region][deliveryDay].bundles.push(bundleData);
          const bundlesLength =
            co2PerRegionDay[region][deliveryDay].bundles.length;
          co2PerRegionDay[region][deliveryDay].sumOfCo2 +=
            bundleData.co2PerDelivery;
          const { sumOfCo2 } = co2PerRegionDay[region][deliveryDay];
          co2PerRegionDay[region][deliveryDay].avgCo2 = sumOfCo2
            ? sumOfCo2 / bundlesLength
            : sumOfCo2;
        } else {
          co2PerRegionDay[region][deliveryDay] = {
            bundles: [bundleData],
            avgCo2: co2PerDelivery || 0,
            sumOfCo2: co2PerDelivery || 0,
          };
        }
      } else {
        co2PerRegionDay[region] = {
          [deliveryDay.toString()]: {
            bundles: [bundleData],
            avgCo2: co2PerDelivery || 0,
            sumOfCo2: co2PerDelivery || 0,
          },
        };
      }
    });
    setCo2Data(co2PerRegionDay);
  };

  const fetchCO2ReportCallback = useCallback((): Promise<
    Co2ReportInterface[]
  > => {
    const [startDate, endDate] = date;
    return fetchCo2DataAsync(startDate, endDate);
  }, [date]);

  const createAndDownloadCSVData = () => {
    if (co2SerializedData) {
      const dateFormat = 'YYYY-MM-DD';
      const startDateDayJS = dayjs(date[0]);
      const endDateDayJS = dayjs(date[1]);
      const avgCo2PerDayPerRegion: Record<string, Record<string, string>> = {};
      let loopDate = dayjs(date[0]);
      while (loopDate.isBefore(endDateDayJS) || loopDate.isSame(endDateDayJS)) {
        const loopDateStringFormat = loopDate.format(dateFormat);
        avgCo2PerDayPerRegion[loopDateStringFormat] = {};
        Object.keys(co2SerializedData)
          .sort((a, b) => sortRegionsForView(a, b))
          .forEach(regionKey => {
            avgCo2PerDayPerRegion[loopDateStringFormat][regionKey] =
              co2SerializedData[regionKey][
                loopDateStringFormat
              ]?.avgCo2.toFixed(2);
          });
        loopDate = loopDate.add(1, 'day');
      }
      const csvRegions = Object.keys(
        avgCo2PerDayPerRegion[Object.keys(avgCo2PerDayPerRegion)[0]]
      )
        .map(region => region)
        .join(',');
      const csvContent = `data:text/csv;charset=utf-8,Date,${csvRegions}\n${Object.keys(
        avgCo2PerDayPerRegion
      )
        .map(
          dateKey =>
            `${dateKey},${Object.keys(avgCo2PerDayPerRegion[dateKey])
              .map(region => avgCo2PerDayPerRegion[dateKey][region])
              .join(',')}`
        )
        .join('\n')}`;
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute(
        'download',
        `co2_report_${startDateDayJS.format(dateFormat)}_${endDateDayJS.format(
          dateFormat
        )}.csv`
      );
      document.body.appendChild(link);
      link.click();
    } else {
      console.warn(
        'Tried to call createAndDownloadCSVData, but co2SerializedData is not defined'
      );
    }
  };
  useEffect(() => {
    fetchCO2ReportCallback()
      .then(result => calcAvgCo2PerRegionDay(result))
      .catch(error => console.log(error));
  }, [date, fetchCO2ReportCallback]);
  if (co2SerializedData) {
    return (
      <div className="container">
        <Helmet>
          <title>Reports - CO2</title>
        </Helmet>
        <div className="co2-report-controls">
          <Flatpickr
            onChange={(selectedDates, representation, instance) =>
              doCheckSelectedPeriod(selectedDates, instance)
            }
            onClose={selectedDates => setDate(selectedDates)}
            options={{
              dateFormat: 'l d M',
              defaultDate: date,
              mode: 'range',
              showMonths: 1,
              shorthandCurrentMonth: true,
              locale: { firstDayOfWeek: 1 },
            }}
            render={(props, ref) => (
              <input
                className="date-picker-container"
                data-input="data-input"
                ref={ref}
              />
            )}
          />
          <ButtonComponent
            onClick={createAndDownloadCSVData}
            text="Download CSV"
          />
        </div>
        <TabsComponent
          variant="scrollable"
          scrollButtons="auto"
          onChange={(ev, value) => setSelectedTab(value)}
          tabsChildren={createTabsWithChart(co2SerializedData)}
          value={selectedTab}
        />
      </div>
    );
  } else {
    return (
      <div className="loader-container">
        <Loader size="xxl" type={'spinner'} />
      </div>
    );
  }
};

export default Co2Report;
